import React, { useEffect, useMemo } from 'react';
import { Modal, Form, Input, Button, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { Card } from '@cards/Card';
import './style.less';

const formLayout = {
  labelCol: {
    span: 6,
  },
  layout: 'vertical',
  wrapperCol: {
    span: 18,
  },
};

function EditMemberModal({ visible, role, onSave, onCancel, form, appUserPathway }) {
  const { t } = useTranslation();
  const { getFieldDecorator, validateFields, setFieldsValue } = form;

  useEffect(() => {
    if (visible && role?.member) {
      setFieldsValue({
        name: role.member.name || '',
        email: role.member.email || '',
        phone: role.member.phone || '',
        businessName: role.member.businessName || '',
      });
    } else if (visible && !role?.member) {
      setFieldsValue({
        name: '',
        email: '',
        phone: '',
        businessName: '',
      });
    }
  }, [visible, role, setFieldsValue]);

  const handleSubmit = e => {
    e.preventDefault();
    validateFields((err, values) => {
      if (!err) {
        onSave({
          name: values.name,
          email: values.email,
          phone: values.phone,
          businessName: values.businessName,
        });
      }
    });
  };

  const rules = useMemo(() => {
    if (!appUserPathway || !appUserPathway.originalPathway) {
      return [];
    }
    const { indexEvents, stages } = appUserPathway.originalPathway;
    const allRules = [];
    indexEvents.forEach(event => {
      allRules.push(...event.rules);
    });
    stages.forEach(stage => {
      allRules.push(...stage.rules);
    });
    return allRules;
  }, [appUserPathway]);

  const availableRuleIds = useMemo(() => new Set(rules.map(r => r.id)), [rules]);

  const availableContentRefs = useMemo(
    () => new Set(rules.map(r => r.whatDetail?.content).filter(Boolean)),
    [rules],
  );

  const callToActionMap = useMemo(() => {
    const map = new Map();
    const ctaList = appUserPathway?.originalPathway?.clientConfiguration?.callToAction || [];
    ctaList.forEach(cta => map.set(cta.id, cta));
    return map;
  }, [appUserPathway]);

  const isCTA = record => record.type === 'cta';
  const isRule = record => record.type === 'rule' || !record.type;

  const columns = useMemo(
    () => [
      {
        title: t('patients:Pathways.careTeam.modal.columns.itemName'),
        dataIndex: 'id',
        key: 'itemName',
        width: 250,
        ellipsis: true,
        render: (id, record) => {
          if (isCTA(record)) {
            const cta = callToActionMap.get(id);
            return cta?.name || id;
          }

          if (isRule(record) && !availableRuleIds.has(id)) {
            return (
              <p className="rule-status-text">
                {t('patients:Pathways.careTeam.modal.ruleRemoved')}
              </p>
            );
          }

          const rule = rules.find(r => r.id === id);
          return rule ? rule.name : id;
        },
      },
      {
        title: t('patients:Pathways.careTeam.modal.columns.value'),
        dataIndex: 'id',
        key: 'value',
        width: 250,
        ellipsis: true,
        render: (id, record) => {
          if (isCTA(record)) {
            return record.contentRef || '';
          }

          if (isRule(record) && !availableRuleIds.has(id)) {
            return null;
          }

          if (isRule(record) && record.contentRef && !availableContentRefs.has(record.contentRef)) {
            return (
              <p className="rule-status-text">
                {t('patients:Pathways.careTeam.modal.contentMismatch')}
              </p>
            );
          }

          const rule = rules.find(r => r.id === id);
          return rule ? rule.whatDetail?.name || rule.whatDetail?.latestVersion?.name : '';
        },
      },
      {
        title: t('patients:Pathways.careTeam.modal.columns.audience'),
        dataIndex: 'id',
        key: 'audience',
        width: 120,
        render: (id, record) => {
          let audience;

          if (isCTA(record)) {
            const cta = callToActionMap.get(id);
            audience = cta?.audience;
          } else {
            const rule = rules.find(r => r.id === id);
            audience = rule?.audienceType;
          }

          if (!audience) return null;

          const audienceKey = `pathways:ProcedurePathways.wizard.careTeamRules.callToAction.audience.${audience}`;
          return t(audienceKey);
        },
      },
      {
        title: t('patients:Pathways.careTeam.modal.columns.permissionType'),
        dataIndex: 'permissionType',
        key: 'permissionType',
        width: 200,
        render: permissionType => {
          const permissionTypeKey = `patients:Pathways.careTeam.modal.permissionTypes.${permissionType}`;
          return t(permissionTypeKey);
        },
      },
    ],
    [t, rules, callToActionMap, availableRuleIds, availableContentRefs],
  );

  const permissionsDataSource =
    role?.permissions?.map((permission, index) => ({
      key: `${permission.id}-${index}`,
      ...permission,
    })) || [];

  return (
    <Modal
      title={t('patients:Pathways.careTeam.modal.title', { roleName: role?.name || '' })}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      className="edit-member-modal"
    >
      {role && (
        <div className="modal-content">
          <Card.Half
            className="role-permissions-card"
            title={t('patients:Pathways.careTeam.modal.permissionsTitle')}
          >
            {role.description && <p className="role-description">{role.description}</p>}
            <Table
              columns={columns}
              dataSource={permissionsDataSource}
              pagination={false}
              size="small"
            />
          </Card.Half>

          <Card.Half
            className="member-details-card"
            title={t('patients:Pathways.careTeam.modal.memberDetailsTitle')}
          >
            <Form {...formLayout} onSubmit={handleSubmit}>
              <Form.Item label={t('patients:Pathways.careTeam.modal.form.name')}>
                {getFieldDecorator('name', {
                  rules: [
                    {
                      required: true,
                      message: t('patients:Pathways.careTeam.modal.form.nameRequired'),
                    },
                  ],
                })(<Input type="text" />)}
              </Form.Item>

              <Form.Item label={t('patients:Pathways.careTeam.modal.form.email')}>
                {getFieldDecorator('email', {
                  rules: [
                    {
                      required: true,
                      message: t('patients:Pathways.careTeam.modal.form.emailRequired'),
                    },
                    {
                      type: 'email',
                      message: t('patients:Pathways.careTeam.modal.form.emailInvalid'),
                    },
                  ],
                })(<Input type="email" />)}
              </Form.Item>

              <Form.Item label={t('patients:Pathways.careTeam.modal.form.phone')}>
                {getFieldDecorator('phone', {
                  rules: [
                    {
                      pattern: /\+[\d\s]+/,
                      message: t('patients:Pathways.careTeam.modal.form.phoneInvalid'),
                    },
                  ],
                })(<Input type="tel" />)}
              </Form.Item>

              <Form.Item label={t('patients:Pathways.careTeam.modal.form.businessName')}>
                {getFieldDecorator('businessName', {
                  rules: [
                    {
                      required: true,
                      message: t('patients:Pathways.careTeam.modal.form.businessNameRequired'),
                    },
                  ],
                })(<Input type="text" />)}
              </Form.Item>

              <div className="modal-footer">
                <Button onClick={onCancel} style={{ marginRight: '10px' }}>
                  {t('patients:Pathways.careTeam.modal.cancel')}
                </Button>
                <Button type="primary" htmlType="submit">
                  {t('patients:Pathways.careTeam.modal.save')}
                </Button>
              </div>
            </Form>
          </Card.Half>
        </div>
      )}
    </Modal>
  );
}

const WrappedEditMemberModal = Form.create()(EditMemberModal);
export default WrappedEditMemberModal;
